// File: src/components/admin/analysis-tasks/AiAssistPanel.js
// AI assistant side panel for analysis task configuration (Issue #64)

import React, { useState, useRef, useEffect } from 'react';
import {
    Drawer, Box, Typography, TextField, Button, CircularProgress,
    Alert, Chip, IconButton, Paper,
} from '@mui/material';
import {
    Close as CloseIcon,
    Send as SendIcon,
    Check as CheckIcon,
} from '@mui/icons-material';
import { adminApi, handleAdminApiError } from '../../../services/adminApi';

const AiAssistPanel = ({ open, onClose, currentConfig, onApplySuggestion }) => {
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const messagesEndRef = useRef(null);

    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages]);

    const handleSend = async (action = 'general_chat', instruction = null) => {
        const text = instruction || input.trim();
        if (!text && action === 'general_chat') return;

        const userMessage = { role: 'user', content: text };
        setMessages(prev => [...prev, userMessage]);
        setInput('');
        setLoading(true);
        setError(null);

        try {
            const conversationHistory = messages.map(m => ({
                role: m.role,
                content: m.content,
            }));

            const result = await adminApi.aiAssistAnalysisTask({
                action,
                instruction: text,
                currentConfig,
                conversationHistory,
            });

            const assistantMessage = {
                role: 'assistant',
                content: result.message || JSON.stringify(result.suggestion, null, 2),
                suggestion: result.suggestion,
                warnings: result.warnings,
                action,
            };
            setMessages(prev => [...prev, assistantMessage]);
        } catch (err) {
            const errorInfo = handleAdminApiError(err);
            setError(errorInfo.message);
        } finally {
            setLoading(false);
        }
    };

    const handleApply = (suggestion) => {
        if (suggestion && onApplySuggestion) {
            onApplySuggestion(suggestion);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            sx={{ '& .MuiDrawer-paper': { width: { xs: '100%', sm: 450 } } }}
            data-testid="ai-assist-panel"
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                {/* Header */}
                <Box sx={{ display: 'flex', alignItems: 'center', p: 2, borderBottom: 1, borderColor: 'divider' }}>
                    <Typography variant="h6" sx={{ flexGrow: 1 }}>AI Assistant</Typography>
                    <IconButton onClick={onClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>

                {/* Quick Actions */}
                <Box sx={{ p: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5, borderBottom: 1, borderColor: 'divider' }}>
                    <Chip
                        label="Generate Prompt"
                        size="small"
                        onClick={() => handleSend('generate_prompt', 'Generate a system prompt based on the current task configuration')}
                        clickable
                        data-testid="ai-generate-prompt"
                    />
                    <Chip
                        label="Refine Prompt"
                        size="small"
                        onClick={() => handleSend('refine_prompt', 'Improve the current system prompt')}
                        clickable
                        data-testid="ai-refine-prompt"
                    />
                    <Chip
                        label="Suggest Tools"
                        size="small"
                        onClick={() => handleSend('suggest_tools', 'Suggest appropriate tools for this task')}
                        clickable
                        data-testid="ai-suggest-tools"
                    />
                    <Chip
                        label="Build Audience"
                        size="small"
                        onClick={() => handleSend('build_audience', 'Suggest audience rules for this task')}
                        clickable
                    />
                    <Chip
                        label="Email Subjects"
                        size="small"
                        onClick={() => handleSend('generate_email_subjects', 'Generate email subject templates for this task')}
                        clickable
                    />
                </Box>

                {/* Messages */}
                <Box sx={{ flexGrow: 1, overflow: 'auto', p: 2 }}>
                    {messages.length === 0 && (
                        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 4 }}>
                            Ask me anything about configuring this analysis task, or use the quick actions above.
                        </Typography>
                    )}
                    {messages.map((msg, idx) => (
                        <Box
                            key={idx}
                            sx={{
                                mb: 2,
                                display: 'flex',
                                justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
                            }}
                        >
                            <Paper
                                sx={{
                                    p: 1.5,
                                    maxWidth: '85%',
                                    backgroundColor: msg.role === 'user' ? 'primary.main' : 'grey.100',
                                    color: msg.role === 'user' ? 'primary.contrastText' : 'text.primary',
                                }}
                            >
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                    {msg.content}
                                </Typography>
                                {msg.warnings && msg.warnings.length > 0 && (
                                    <Box sx={{ mt: 1 }}>
                                        {msg.warnings.map((w, i) => (
                                            <Alert key={i} severity="warning" sx={{ py: 0, mb: 0.5, fontSize: '0.75rem' }}>
                                                {w.message}
                                            </Alert>
                                        ))}
                                    </Box>
                                )}
                                {msg.suggestion && msg.role === 'assistant' && (
                                    <Button
                                        size="small"
                                        startIcon={<CheckIcon />}
                                        onClick={() => handleApply(msg.suggestion)}
                                        sx={{ mt: 1 }}
                                        variant="outlined"
                                        data-testid="ai-assist-apply"
                                    >
                                        Apply
                                    </Button>
                                )}
                            </Paper>
                        </Box>
                    ))}
                    {loading && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                            <CircularProgress size={24} />
                        </Box>
                    )}
                    {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
                    <div ref={messagesEndRef} />
                </Box>

                {/* Input */}
                <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Ask about task configuration..."
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={handleKeyDown}
                        multiline
                        maxRows={4}
                        disabled={loading}
                        InputProps={{
                            endAdornment: (
                                <IconButton
                                    onClick={() => handleSend()}
                                    disabled={loading || !input.trim()}
                                    size="small"
                                >
                                    <SendIcon />
                                </IconButton>
                            ),
                        }}
                    />
                </Box>
            </Box>
        </Drawer>
    );
};

export default AiAssistPanel;
